import { RefObject, useCallback, useMemo } from "react";
import html2pdf from "html2pdf.js";
import logo from "../../images/as-logo.png";
import lohLogo from "../../images/loh-logo.png";
import {
    useAppSettingsQuery,
    useFormDataArchiveQuery,
    useFormQuery,
    useOfficeQuery,
    useTissueQuery,
} from "../../queries";
import { useCurrentCase } from "../../hooks";
import { displayCurrentDateAndTime, displayDate, getDonorIsbtNumber } from "../../helper-functions";
import { useNavigate, useParams } from "react-router-dom";
import { FormData as DbFormData, FormSlugs, QueryType } from "../../models";
import { useToasts } from "../../contexts";
import { transformTextareasOnDownload } from "./FormConfig";

interface UseDownloadFormProps {
    baseFileName: string;
    formRef: RefObject<any>;
    formData: DbFormData | undefined;
    refetchFormData?: () => Promise<void>;
    toEmail?: string;
    formSlugProp?: string;
}

interface UseDownloadForm {
    downloadPdf: () => void;
    archivePdf: () => void;
    emailPdf: () => void;
    generatePdfFile: (htmlContent: string) => Promise<string | null>;
}

export function useDownloadForm({
    baseFileName,
    formRef,
    formData,
    refetchFormData,
    toEmail,
    formSlugProp,
}: Readonly<UseDownloadFormProps>): UseDownloadForm {
    const { errorToast } = useToasts();
    const navigate = useNavigate();
    const {
        listSlug,
        formDataArchiveId,
        formSlug: formSlugFromParams,
        formDataId,
    } = useParams<{
        listSlug: string;
        formDataArchiveId: string;
        formSlug: string;
        formDataId: string;
    }>();
    const formSlug = formSlugFromParams ?? formSlugProp;
    const {
        myAppSettingsQuery: { data: appSettings },
    } = useAppSettingsQuery();
    const { uploadMutation, addFileMutation } = useFormDataArchiveQuery({
        formDataId: formData?.id,
        id: formDataArchiveId,
    });

    const { case: _case, caseId, referral } = useCurrentCase({ includeDetails: true });
    const donorNumber = _case?.donor?.number;
    const intentForEyes = _case?.referral?.intentForEyes;
    const {
        listQuery: { data: offices },
    } = useOfficeQuery({ queryType: QueryType.List });
    const tissueId = formData?.tissueId;
    const {
        productCodeQuery: { data: productCodeData },
    } = useTissueQuery({ queryType: "productCode", id: tissueId });
    const productCode = productCodeData?.productCode;
    const { sendFileMutation } = useFormQuery({
        id: formData?.formId,
        formSlug: formSlug,
        toEmail: toEmail,
    });

    const getLogo = useCallback(
        (logoId: string) => {
            if (logoId === "LOH") {
                return lohLogo;
            }

            return appSettings?.logoUri?.length ? appSettings?.logoUri : logo;
        },
        [appSettings]
    );

    const revisionDate = useMemo(() => {
        const createdOn = formData?.form?.createdOn;
        if (!createdOn) return "";
        return `r_${displayDate(createdOn)}`.replace(/-/g, "/");
    }, [formData?.form?.createdOn]);

    const getFileName = useCallback(() => {
        const now = new Date();
        const date = now.toISOString().split("T")[0];
        const epoch = now.getTime();
        return `${baseFileName}_${date}_${epoch}.pdf`;
    }, [baseFileName]);

    const getOptions = useCallback(
        (filename: string) => {
            const addressInFooter = formData?.form?.formConfig?.addressFontSize ?? 0;
            const topBottomMargin = addressInFooter > 0 ? 12 : 10;
            const options = {
                margin: [topBottomMargin, 5],
                filename,
                pagebreak: { mode: "avoid-all" },
                jsPDF: {
                    format: "letter",
                    orientation: "portrait",
                    putTotalPages: true,
                    floatPrecision: "16",
                },
                html2canvas: {
                    scale: 4,
                    useCORS: true,
                    letterRendering: true,
                    removeContainer: true,
                },
            };
            return options;
        },
        [formData?.form?.formConfig?.addressFontSize]
    );

    const lohAddress = "Legacy of Hope, 502 20th Street South, Birmingham, AL 35233, (800) 252-3677";
    const asnAddress = "Advancing Sight Network, 500 Robert Jemison Road, Birmingham, AL 35209, (800) 423-7811";

    const opoNumber = useMemo(() => {
        return _case?.referral?.caseFileIdentifier ?? "";
    }, [_case?.referral?.caseFileIdentifier]);

    const getTerritoryContactText = useCallback(
        (territoryLabel: string | undefined) => {
            if (territoryLabel === undefined) return "";
            const mainOffice = offices?.find((o) => o.territory?.label === territoryLabel && o.isMainOffice);

            if (!mainOffice) return "";
            const addressParts = [
                mainOffice.address1,
                mainOffice.address2,
                mainOffice.city,
                mainOffice.state,
                mainOffice.zip,
            ].filter((part) => part !== null && part?.trim() !== "");
            let contact = addressParts.join(", ");

            const otherParts = [mainOffice.phone, mainOffice.fax, mainOffice.email].filter(
                (part) => part !== null && part?.trim() !== ""
            );

            contact = contact + " | " + otherParts.join(" | ");
            return contact;
        },
        [offices]
    );

    const addFooterAndLogo = useCallback(
        (pdf: any) => {
            const totalPages = pdf.internal.getNumberOfPages();
            const formConfig = formData?.form?.formConfig;
            const lastLineY = pdf.internal.pageSize.getHeight() - 3;

            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFontSize(10);
                pdf.text(`Page ${i} of ${totalPages}`, pdf.internal.pageSize.getWidth() - 25, lastLineY);
                if (
                    formConfig &&
                    formConfig.addressFontSize > 0 &&
                    formConfig.lohAddressOffsetX > 0 &&
                    formConfig.lohAddressOffsetY > 0
                ) {
                    pdf.setFontSize(formConfig.addressFontSize);
                    pdf.text(
                        lohAddress,
                        pdf.internal.pageSize.getWidth() - formConfig.lohAddressOffsetX,
                        pdf.internal.pageSize.getHeight() - formConfig.lohAddressOffsetY
                    );
                }
                if (
                    formConfig &&
                    formConfig.addressFontSize > 0 &&
                    formConfig.asnAddressOffsetX > 0 &&
                    formConfig.asnAddressOffsetY > 0
                ) {
                    pdf.text(
                        asnAddress,
                        pdf.internal.pageSize.getWidth() - formConfig.asnAddressOffsetX,
                        pdf.internal.pageSize.getHeight() - formConfig.asnAddressOffsetY
                    );
                }
                if (
                    formConfig &&
                    formConfig.caseNumFontSize > 0 &&
                    formConfig.caseNumOffsetX > 0 &&
                    formConfig.caseNumOffsetY > 0
                ) {
                    pdf.setFontSize(formConfig.caseNumFontSize);
                    pdf.text(
                        `Legacy of Hope Case #: ${opoNumber}`,
                        pdf.internal.pageSize.getWidth() - formConfig.caseNumOffsetX,
                        pdf.internal.pageSize.getHeight() - formConfig.caseNumOffsetY
                    );
                }
                if (
                    formConfig &&
                    formConfig.caseNumFontSize > 0 &&
                    formConfig.unosOffsetX > 0 &&
                    formConfig.unosOffsetY > 0
                ) {
                    pdf.text(
                        `UNOS ID#: ${opoNumber}`,
                        pdf.internal.pageSize.getWidth() - formConfig.unosOffsetX,
                        pdf.internal.pageSize.getHeight() - formConfig.unosOffsetY
                    );
                }
                if (formConfig?.hasDonorNumber) {
                    pdf.text(`Donor# ${donorNumber ?? ""}`, pdf.internal.pageSize.getWidth() * 0.5, lastLineY);
                }

                if (i === totalPages) {
                    if (formConfig?.hasIntentForEyes) {
                        pdf.text(`Printed ${displayCurrentDateAndTime()}`, 5, pdf.internal.pageSize.getHeight() - 9);
                        pdf.text(revisionDate, 5, pdf.internal.pageSize.getHeight() - 6);
                        pdf.text(`Intent for tissue: ${intentForEyes ?? ""}`, 5, lastLineY);
                    } else {
                        pdf.text(`Printed ${displayCurrentDateAndTime()}`, 5, pdf.internal.pageSize.getHeight() - 6);
                        pdf.text(revisionDate, 5, lastLineY);
                        if (formSlug === FormSlugs.DED) {
                            pdf.text(
                                `Referral ID - ${_case?.referral?.number}`,
                                pdf.internal.pageSize.getWidth() / 2,
                                pdf.internal.pageSize.getHeight() - 6
                            );
                        }
                        if (formSlug === FormSlugs.PostOp3To6Month) {
                            pdf.text(
                                `DIN: ${getDonorIsbtNumber(donorNumber, referral?.territory)}`,
                                pdf.internal.pageSize.getWidth() / 2,
                                pdf.internal.pageSize.getHeight() - 6
                            );
                            pdf.text(`Product Code: ${productCode}`, pdf.internal.pageSize.getWidth() / 2, lastLineY);
                        }
                        if (formSlug === FormSlugs.TIF) {
                            const territories = [
                                { label: "ASN (R-P-S-FD-TE-DE)", code: "ADS" },
                                { label: "MSEB (R-S-FD-TE-DE)", code: "MSEB" },
                                { label: "MLEB (R-S-FD-TE-DE)", code: "MLEB" },
                                { label: "SEB (R-P-S-FD-TE-DE)", code: "SEB" },
                            ];

                            let xPos = pdf.internal.pageSize.getWidth() - 185;
                            const territory = _case?.referral?.territory?.label;
                            const territoryContactText = getTerritoryContactText(_case?.referral?.territory?.label);

                            const drawTerritoryText = (label: string, isBold: boolean) => {
                                pdf.setFont("Helvetica", isBold ? "bold" : "normal");
                                const width = pdf.getTextWidth(label);
                                pdf.text(label, xPos, pdf.internal.pageSize.getHeight() - 15);
                                return width;
                            };

                            territories.forEach((t) => {
                                const isBold = territory === t.code;
                                const width = drawTerritoryText(t.label, isBold);
                                xPos += width + 3;
                            });
                            pdf.setFont("Helvetica", "normal");
                            pdf.setFontSize(8);
                            pdf.text(
                                territoryContactText,
                                pdf.internal.pageSize.getWidth() - 182,
                                pdf.internal.pageSize.getHeight() - 12
                            );
                            pdf.text(
                                "All are accredited members of the EBAA and registered with the FDA as a human tissue facility.",
                                pdf.internal.pageSize.getWidth() - 170,
                                pdf.internal.pageSize.getHeight() - 9
                            );
                        }
                    }
                } else {
                    if (formConfig?.hasIntentForEyes) {
                        pdf.text(`Intent for tissue: ${intentForEyes ?? ""}`, 5, lastLineY);
                    }
                }
                if (i == 1) {
                    if (formConfig) {
                        const x = formConfig.x > 0 ? formConfig.x : pdf.internal.pageSize.getWidth() + formConfig.x;
                        const y = formConfig.y > 0 ? formConfig.y : pdf.internal.pageSize.getHeight() + formConfig.y;
                        const width =
                            formConfig.width > 0
                                ? formConfig.width
                                : pdf.internal.pageSize.getWidth() + formConfig.width;
                        const height =
                            formConfig.height > 0
                                ? formConfig.height
                                : pdf.internal.pageSize.getHeight() + formConfig.height;
                        pdf.addImage(getLogo(formConfig.image), "PNG", x, y, width, height);
                    } else {
                        pdf.addImage(getLogo(""), "PNG", pdf.internal.pageSize.getWidth() - 55, 2, 52.3, 9.6);
                    }
                }
            }
            return pdf;
        },
        [
            formData?.form?.formConfig,
            opoNumber,
            donorNumber,
            revisionDate,
            intentForEyes,
            formSlug,
            _case?.referral?.number,
            _case?.referral?.territory?.label,
            referral?.territory,
            productCode,
            getTerritoryContactText,
            getLogo,
        ]
    );

    const getTIFContent = (pdf: any) => {
        const totalPages = pdf.internal.getNumberOfPages();

        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            if (i == totalPages) {
                const margin = 8;
                const spaceBetweenParagraphs = 2;
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();
                pdf.addPage();

                pdf.setFontSize(14);
                pdf.text("Package Insert Section", pdf.internal.pageSize.getWidth() / 2, margin + 10, {
                    align: "center",
                });

                pdf.setFontSize(9);

                const paragraphs = [
                    [
                        {
                            text: `Advancing Sight Network supplies the following information to assist surgeons and/or consignee in the evaluation of eye tissue for transplantation. The enclosed ocular tissue has been evaluated by Advancing Sight Network using the Eye Bank Association of America (EBAA) Medical Standards and Food and Drug Administration(FDA) regulations.`,
                            bold: false,
                        },
                    ],
                    [
                        {
                            text: `Current regulations require infectious testing for the following diseases: HIV 1, HIV 2, HBV, HCV, and Syphilis. All infectious disease tests were performed by CLIA certified (1988 (42 U.S.C. 263a and 42 CFR Part 493) and FDA registered laboratories. FDA approved screening tests were used for infectious disease testing as required by the FDA and EBAA, some of which are approved for pre-mortem blood and that FDA approved tests for cadaveric blood were used where available. The results shall be non-reactive or negative before tissue is distributed for transplantation and are documented on the Tissue Information Form (TIF). Certain other non- required tests may be reactive, but the tissue is still considered safe for transplant.`,
                            bold: false,
                        },
                    ],
                    [
                        {
                            text: `As part of Advancing Sight Network's standard operating procedure, the following donor-related examinations have been performed: 1) examination of medical records, 2) consultation with medical personnel having specific medical knowledge of the donor, when necessary, 3) donor risk assessment interview with the next-of kin or other relative, primary treating physician, or person with an affinity relationship with the donor, 4) donor body physical assessment, 5) slit-lamp microscopic evaluation, 6) specular microscopic evaluation, 7) examination of gross physical autopsy results (if applicable), 8) death investigation report (if applicable), and 9) consultation with an eye bank Medical Director(s) (if applicable).`,
                            bold: false,
                        },
                    ],
                    [
                        {
                            text: `If the tissue has been returned after placement, the accepting surgeon will be notified. The returned tissue is only suitable for re-distribution after appropriate transportation, storage and evaluation of the tissue is found to be acceptable by trained staff.`,
                            bold: false,
                        },
                    ],
                    [
                        {
                            text: `In accordance with EBAA, FDA and Advancing Sight Network guidelines, the donor has been deemed acceptable to donate for transplantation based on the results of screening and testing requirements to determine donor eligibility. The tissue is delivered with no warranty as to merchantability or fitness for a particular purpose, and the recipient waives all claims he or she may have for breach of warranty, either expressed or implied.`,
                            bold: false,
                        },
                        {
                            text: ` The final responsibility for determining the suitability of the tissue for transplantation rests with the surgeon.`,
                            bold: true,
                        },
                    ],
                    [
                        {
                            text: `The preservation medium information for each tissue distributed for transplantation shall be found on the Tissue Information section of this form and on the tissue label.`,
                            bold: false,
                        },
                    ],
                    [
                        {
                            text: `- Corneal tissue preserved in FDA approved, intermediate-term corneal preservation medium is stored at 2-8 degrees Celsius, and is suitable for allograft transplantation for up to 14 days after preservation. The expiration date and time should be examined on the Tissue Information Form and the tissue label before use to ensure that it still may be able to be transplanted. Expired tissue may not be used for transplant. According to the guidelines from the manufacturer, check for preservation medium color change. Color change may indicate a change in pH or contamination; the tissue should not be used, and Advancing Sight Network should be immediately notified.`,
                            bold: false,
                        },
                        {
                            text: `Do Not Freeze!`,
                            bold: true,
                        },
                    ],
                    [
                        {
                            text: `- Sclera preserved in ethyl alcohol is stored at ambient temperature, and is suitable for surgical use for up to one year after preservation. The expiration date and time should be examined on the Tissue Information Form and the tissue label before use to ensure that it still may be able to be transplanted. Expired tissue may not be used for transplant. Ethyl alcohol should be free of debris and/or cloudiness. Any change in the appearance may indicate contamination, in which case the tissue should not be used, and Advancing Sight Network should be immediately notified. Do Not Freeze! Sclera preserved in alcohol may be rehydrated by a 20-minute soak in at least 30cc of sterile balanced salt solution (BSS). Thirty milligrams of broad-spectrum antibiotic may be added to BSS during rehydration.`,
                            bold: false,
                        },
                    ],
                    [
                        {
                            text: `A tamper-evident seal is applied to every tissue container. The surgeon or his/her designee should always check for integrity of the seal. If the integrity of a seal is compromised and/or any evidence of possible tampering is observed, Advancing Sight Network should be immediately notified.`,
                            bold: false,
                        },
                    ],
                    [
                        {
                            text: `Advancing Sight Network does not perform pre-surgical microbiologic cultures.`,
                            bold: true,
                        },
                    ],
                    [
                        {
                            text: `It cannot be assumed that the preservation medium is bactericidal or fungicidal. Culturing the medium or the corneo-scleral rim at the time of the surgery is at the discretion of the surgeon.`,
                            bold: false,
                        },
                    ],
                    [
                        {
                            text: `The consignee [any eye bank, eye banking intermediary (whether individual, agency, institution, or organization) that receives tissue and assumes responsibility for any step in the processing, storage, distribution and/or use of such tissue] agrees to accept responsibility for reimbursement of Advancing Sight Network processing fees, either via the surgical facility or the office of the transplanting surgeon. Exceptions may be made for indigent patients after completing and submitting a Processing Fee Waiver (supplied upon request) at least two weeks prior to date of surgery and after approval from Advancing Sight Network.`,
                            bold: false,
                        },
                    ],
                    [
                        {
                            text: `The consignee agrees to participate in Advancing Sight Network's tracking system, enabling every tissue to be tracked from donor to recipient. Please thoroughly complete the Recipient Information Form and return it to Advancing Sight Network or the distributing eye bank promptly including information containing: 1) tissue recipient's name and unique identification number, 2) age and/or date or birth, diagnosis, date of surgery, location of surgery, type of surgery, 3) name of the transplanting surgeon when the tissue is transplanted, and 4) ISBT 128 identifier. Complete and return the Adverse Reaction Report only if complications develop that are suspected to be due to the donor's tissue or a communicable disease. Post-operative follow-up information requests shall be sent periodically to identify any unreported Adverse Reactions. Adverse Reaction investigations may need to be reported to EBAA and/or FDA. Any questions regarding this tissue or any technical issue concerning the use of the tissue, please contact the technical department of Advancing Sight Network.`,
                            bold: false,
                        },
                    ],
                ];

                let yPosition = margin + 20;
                const lineHeight = 4;

                paragraphs.forEach((paragraphGroup) => {
                    let currentLine = " ";

                    paragraphGroup.forEach((segment) => {
                        const words = segment.text.split(" ");

                        words.forEach((word) => {
                            const testLine = currentLine + word + " ";
                            const testLineWidth = pdf.getTextWidth(testLine);

                            // Check if the current line would exceed the page width
                            if (testLineWidth > pageWidth - 2 * margin) {
                                // Check if adding this line exceeds the page height
                                if (yPosition + lineHeight > pageHeight - margin) {
                                    pdf.addPage();
                                    yPosition = margin + 20;
                                }

                                pdf.setFont("Helvetica", segment.bold ? "bold" : "normal");
                                pdf.text(currentLine, margin, yPosition);
                                yPosition += lineHeight;
                                currentLine = word + " ";
                            } else {
                                currentLine = testLine;
                            }
                        });

                        if (currentLine) {
                            pdf.setFont(segment.bold ? "Helvetica" : "Helvetica", segment.bold ? "bold" : "normal");
                            pdf.text(currentLine, margin, yPosition);
                            yPosition += lineHeight;
                        }

                        currentLine = " ";
                    });

                    yPosition += spaceBetweenParagraphs;
                });
            }
        }
    };

    const getMedicalRecordRequestContent = (pdf: any) => {
        const totalPages = pdf.internal.getNumberOfPages();

        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            if (i == totalPages) {
                const margin = 10;
                const spaceBetweenParagraphs = 5;
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();

                pdf.setFontSize(9);

                const paragraphs = [
                    [
                        {
                            text: `The Advancing Sight Network and partner OPO are subject to the exemption specified in the Health Insurance Portability and Accountability Act (HIPPA), section § 164.512(h). For the purposed of uses and disclosures for cadaveric organ, eye, and tissue donation purposes, a covered entity may use or disclose protected health information to organ procurement organizations or other entities engaged in the procurement, banking, or tranplantation of cadaveric organs, eyes, or tissues for the purposes of facilitating organ, eye, and tissue transplantation.`,
                            italic: true,
                        },
                    ],
                    [
                        {
                            text: `NOTE: This message is intended for the use of the individual or entity to whom it is addressed and may contain information that is privileged, confidential and exempt from disclosure under applicable law. If the reader of this message is not the intended recipient or the employee or agent responsible for delivering the message to the intended recipient, you are hereby notified that any dissemination, distribution, or copying of this communication is strictly prohibited. If you have received this communication in error, please notify us immediately by phone and return the original message to us at the above address. Thank you. IF YOU HAVE ANY PROBLEMS RECEIVING THIS FAX PLEASE CALL AS SOON AS POSSIBLE AT THE NUMBER ABOVE.`,
                            italic: false,
                        },
                    ],
                ];

                let yPosition = pageHeight - 60;
                const lineHeight = 4;

                paragraphs.forEach((paragraphGroup) => {
                    let currentLine = " ";

                    paragraphGroup.forEach((segment) => {
                        const words = segment.text.split(" ");

                        words.forEach((word) => {
                            const testLine = currentLine + word + " ";
                            const testLineWidth = pdf.getTextWidth(testLine);

                            // Check if the current line would exceed the page width
                            if (testLineWidth > pageWidth - 2 * margin) {
                                // Check if adding this line exceeds the page height
                                if (yPosition + lineHeight > pageHeight - margin) {
                                    pdf.addPage();
                                    yPosition = margin + 20;
                                }

                                pdf.setFont("Helvetica", segment.italic ? "italic" : "normal");
                                pdf.text(currentLine, margin, yPosition);
                                yPosition += lineHeight;
                                currentLine = word + " ";
                            } else {
                                currentLine = testLine;
                            }
                        });

                        // Add any remaining text in the current line
                        if (currentLine) {
                            pdf.setFont(
                                segment.italic ? "Helvetica" : "Helvetica",
                                segment.italic ? "italic" : "normal"
                            );
                            pdf.text(currentLine, margin, yPosition);
                            yPosition += lineHeight;
                        }

                        currentLine = " ";
                    });

                    yPosition += spaceBetweenParagraphs;
                });
            }
        }
    };

    const addAdditionalContent = useCallback(
        (pdf: any) => {
            if (formSlug === FormSlugs.TIF) {
                getTIFContent(pdf);
            }
            if (formSlug === FormSlugs.MedicalRecordsRequest) {
                getMedicalRecordRequestContent(pdf);
            }
        },
        [formSlug]
    );

    const transformContent = useCallback(
        (originalStyles: Map<any, any>, elements: any) => {
            if (transformTextareasOnDownload(formSlug)) {
                elements.forEach((element: HTMLElement) => {
                    originalStyles.set(element, element.style.cssText);

                    if (formSlug == FormSlugs.TissueRecipientInfo) {
                        element.style.fontSize = "10px";
                    }

                    if (element instanceof HTMLInputElement && element.type === "text") {
                        element.style.paddingTop = "0";
                        element.style.height = "30px";
                    } else if (element instanceof HTMLTextAreaElement) {
                        const wrapper = document.createElement("div");
                        wrapper.style.lineHeight = "1.2";
                        wrapper.style.paddingLeft = "10px";
                        wrapper.style.padding = "6px";
                        wrapper.style.height = "auto";
                        wrapper.style.minHeight = "50px";
                        wrapper.style.minWidth = "100%";
                        if (formSlug == FormSlugs.TissueRecipientInfo) {
                            wrapper.style.border = "1px solid rgb(204, 204, 204)";
                            wrapper.style.borderColor = "rgb(211, 211, 211)";
                            wrapper.style.borderRadius = "5px";
                        }
                        wrapper.style.wordBreak = "break-word";
                        wrapper.innerHTML = element.value.replace(/\n/g, "<br>");

                        const parent = element.parentNode;
                        if (parent) {
                            element.style.display = "none";
                            parent.insertBefore(wrapper, element);
                        }
                    }
                });
            }
        },
        [formSlug]
    );

    const restoreTransformedContent = useCallback(
        (originalStyles: Map<any, any>, elements: any, originalFontSize: any) => {
            if (transformTextareasOnDownload(formSlug)) {
                elements.forEach((element: HTMLElement) => {
                    const stored = originalStyles.get(element);
                    if (element.previousElementSibling?.tagName === "DIV" && element instanceof HTMLTextAreaElement) {
                        element.previousElementSibling.remove();
                        element.style.display = "";
                    }
                    element.style.cssText = stored || "";
                });
            }
        },
        [formSlug]
    );

    const downloadPdf = useCallback(() => {
        if (!formRef.current) return;

        const content = formRef.current.cloneNode?.(true);
        if (!content) return;

        const fileName = getFileName();
        const originalStyles = new Map();
        const elements = content.querySelectorAll("*");
        const originalFontSize = content.style.fontSize;

        transformContent(originalStyles, elements);

        html2pdf()
            .set(getOptions(fileName))
            .from(content)
            .toPdf()
            .get("pdf")
            .then((pdf: any) => addFooterAndLogo(pdf))
            .then((pdf: any) => addAdditionalContent(pdf))
            .save()
            .catch((e: unknown) => {
                console.error(e);
                errorToast("There was a problem downloading the form.");
            })
            .finally(() => {
                restoreTransformedContent(originalStyles, elements, originalFontSize);
            });
    }, [
        formRef,
        getFileName,
        transformContent,
        getOptions,
        addFooterAndLogo,
        addAdditionalContent,
        errorToast,
        restoreTransformedContent,
    ]);

    const archivePdf = useCallback(() => {
        if (!formRef.current) return;

        const content = formRef.current.cloneNode?.(true);
        if (!content) return;

        const fileName = getFileName();
        const originalStyles = new Map();
        const elements = content.querySelectorAll("*");
        const originalFontSize = content.style.fontSize;

        transformContent(originalStyles, elements);

        html2pdf()
            .set(getOptions(fileName))
            .from(content)
            .toPdf()
            .get("pdf")
            .then((pdf: any) => addFooterAndLogo(pdf))
            .get("pdf")
            .then((pdf: any) => {
                const uriString = pdf.output("datauristring");
                const base64 = uriString.split("base64,")[1];
                const binaryData = atob(base64);
                const byteArray = new Uint8Array(binaryData.length);
                for (let i = 0; i < binaryData.length; i++) {
                    byteArray[i] = binaryData.charCodeAt(i);
                }
                const file = new File([byteArray], fileName, { type: "application/pdf" });
                const formData = new FormData();
                formData.append("file", file);
                if (formDataArchiveId) {
                    addFileMutation
                        .mutateAsync(formData)
                        .then(() => {
                            if (refetchFormData) {
                                refetchFormData();
                            }
                        })
                        .then(() => {
                            navigate(`/lists/${listSlug}/cases/${caseId}/forms/${formSlug}/form-data/${formDataId}`);
                        })
                        .catch((e: unknown) => {
                            errorToast(e);
                        });
                } else {
                    uploadMutation
                        .mutateAsync(formData)
                        .then(() => {
                            if (refetchFormData) {
                                refetchFormData();
                            }
                        })
                        .catch((e: unknown) => {
                            errorToast(e);
                        });
                }
            })
            .catch((e: unknown) => {
                console.error(e);
                errorToast("There was a problem archiving the form.");
            })
            .finally(() => {
                restoreTransformedContent(originalStyles, elements, originalFontSize);
            });
    }, [
        formRef,
        getFileName,
        transformContent,
        getOptions,
        addFooterAndLogo,
        formDataArchiveId,
        addFileMutation,
        refetchFormData,
        navigate,
        listSlug,
        caseId,
        formSlug,
        formDataId,
        errorToast,
        uploadMutation,
        restoreTransformedContent,
    ]);

    const emailPdf = useCallback(() => {
        if (!formRef.current) return;

        const content = formRef.current.cloneNode?.(true);
        if (!content) return;

        const fileName = getFileName();
        const originalStyles = new Map();
        const elements = content.querySelectorAll("*");
        const originalFontSize = content.style.fontSize;

        transformContent(originalStyles, elements);

        html2pdf()
            .set(getOptions(fileName))
            .from(content)
            .toPdf()
            .get("pdf")
            .then((pdf: any) => addFooterAndLogo(pdf))
            .then((pdf: any) => addAdditionalContent(pdf))
            .get("pdf")
            .then((pdf: any) => {
                const uriString = pdf.output("datauristring");
                const base64 = uriString.split("base64,")[1];
                const binaryData = atob(base64);
                const byteArray = new Uint8Array(binaryData.length);
                for (let i = 0; i < binaryData.length; i++) {
                    byteArray[i] = binaryData.charCodeAt(i);
                }
                const file = new File([byteArray], fileName, { type: "application/pdf" });
                const formData = new FormData();
                formData.append("file", file);
                sendFileMutation.mutateAsync(formData);
            })
            .catch((e: unknown) => {
                console.error(e);
                errorToast("There was a problem sending email.");
            })
            .finally(() => {
                restoreTransformedContent(originalStyles, elements, originalFontSize);
            });
    }, [
        formRef,
        getFileName,
        transformContent,
        getOptions,
        addFooterAndLogo,
        addAdditionalContent,
        sendFileMutation,
        errorToast,
        restoreTransformedContent,
    ]);

    const generatePdfFile = useCallback(
        async (htmlContent: string): Promise<string | null> => {
            if (!htmlContent) return null;

            try {
                const fileName = getFileName();

                const pdf = await html2pdf()
                    .set(getOptions(fileName))
                    .from(htmlContent)
                    .toPdf()
                    .get("pdf")
                    .then((pdf: any) => addFooterAndLogo(pdf))
                    .then((pdf: any) => addAdditionalContent(pdf))
                    .get("pdf");

                const base64String = pdf.output("datauristring").split("base64,")[1];

                return base64String;
            } catch (e) {
                console.error(e);
                errorToast("There was a problem generating the PDF.");
                return null;
            }
        },
        [getFileName, getOptions, addFooterAndLogo, addAdditionalContent, errorToast]
    );

    return useMemo(
        () => ({
            downloadPdf,
            archivePdf,
            emailPdf,
            generatePdfFile,
        }),
        [archivePdf, downloadPdf, emailPdf, generatePdfFile]
    );
}
